import { Plus } from "lucide-react";
import {
  useDataProvider,
  useGetList,
  useNotify,
  useRefresh,
  useTranslate,
} from "ra-core";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import type { NevoraDataProvider } from "../datos/dataProvider";
import { useEsAdmin } from "../datos/useEsAdmin";
import { useResponsables } from "../datos/useResponsables";
import { textoComision } from "../dominio/comisiones";
import { claveEmbudo, ErrorEtapa } from "../dominio/etapas";
import type { Cobro, ComisionFila, Negocio } from "../dominio/tipos";
import { CobroDialog } from "./CobroDialog";
import { EtiquetaEtapa } from "./EtiquetaEtapa";
import { formatoFecha, formatoImporte } from "./formato";
import { useAbrirFicha } from "./useAbrirFicha";

/**
 * Proyectos de un cliente y cobros del proyecto en foco (S8, Robert 02/10/2026). Cobrado =
 * primer 50 %; el modelo admite el segundo cobro. Cada cobro enseña la comisión que ha
 * generado (S9: 5 % de la base sin IVA para quien agendó la consultoría) o por qué no la
 * lleva. La compra nueva de un cliente crea un proyecto en Ventas · Preparando propuesta
 * (solo uno abierto en Ventas por cliente). Cobros y compra nueva, solo Jan y Robert (S10):
 * el setter ve sus proyectos, no los importes.
 */
export const ProyectosFicha = ({ negocio }: { negocio: Negocio }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const esAdmin = useEsAdmin();
  const abrirFicha = useAbrirFicha();
  const dataProvider = useDataProvider<NevoraDataProvider>();
  const [cobroAbierto, setCobroAbierto] = useState(false);
  const [nombreNuevo, setNombreNuevo] = useState("");
  const [creando, setCreando] = useState(false);

  const { data: proyectos } = useGetList<Negocio>("proyectos_vista", {
    pagination: { page: 1, perPage: 50 },
    sort: { field: "proyecto_id", order: "ASC" },
    filter: { negocio_id: negocio.negocio_id },
  });
  const { data: cobros } = useGetList<Cobro>(
    "cobros",
    {
      pagination: { page: 1, perPage: 50 },
      sort: { field: "numero", order: "ASC" },
      filter: { proyecto_id: negocio.proyecto_id },
    },
    { enabled: esAdmin },
  );
  // 40: la comisión de cada persona por cada cobro (setter y consultor).
  const { data: comisiones } = useGetList<ComisionFila>(
    "comisiones_vista",
    {
      pagination: { page: 1, perPage: 200 },
      sort: { field: "id", order: "ASC" },
      filter: { proyecto_id: negocio.proyecto_id },
    },
    { enabled: esAdmin && !!cobros?.length },
  );
  const { nombre } = useResponsables();

  const hayVentas = (proyectos ?? []).some((p) => p.embudo === "ventas");
  const totalCobrado = (cobros ?? []).reduce(
    (s, c) => s + Number(c.importe),
    0,
  );

  const crearProyecto = async () => {
    setCreando(true);
    try {
      await dataProvider.nuevoProyecto(negocio.negocio_id, nombreNuevo);
      notify("nevora.proyectos.creado", { type: "success" });
      setNombreNuevo("");
      refresh();
    } catch (e) {
      notify(
        e instanceof ErrorEtapa
          ? e.clave
          : e instanceof Error
            ? e.message
            : "ra.notification.http_error",
        { type: e instanceof ErrorEtapa ? "warning" : "error" },
      );
    } finally {
      setCreando(false);
    }
  };

  return (
    <section
      id="proyectos"
      className="rounded-lg border bg-card p-4 md:p-5 space-y-4"
    >
      <h2 className="titulo-seccion">
        {translate("nevora.ficha.secciones.proyectos")}
      </h2>

      {(proyectos?.length ?? 0) > 1 ? (
        <ul
          className="space-y-2"
          aria-label={translate("nevora.proyectos.titulo")}
        >
          {proyectos!.map((p) => {
            const enFoco =
              String(p.proyecto_id) === String(negocio.proyecto_id);
            return (
              <li
                key={String(p.proyecto_id)}
                className="flex flex-wrap items-center gap-2 text-sm"
              >
                <span className="text-muted-foreground w-16">
                  {translate(claveEmbudo(p.embudo))}
                </span>
                <EtiquetaEtapa etapa={p.etapa} />
                {p.proyecto_nombre ? (
                  <span className="font-medium">{p.proyecto_nombre}</span>
                ) : null}
                {enFoco ? (
                  <span className="text-xs text-muted-foreground">
                    · {translate("nevora.proyectos.principal")}
                  </span>
                ) : (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => abrirFicha(p.negocio_id, p.proyecto_id)}
                  >
                    {translate("nevora.proyectos.ver")}
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      ) : null}

      {esAdmin ? (
        <div className="space-y-2">
          <h3 className="etiqueta">{translate("nevora.proyectos.cobros")}</h3>
          {negocio.importe_total != null ? (
            <p className="text-sm">
              {translate("nevora.proyectos.importe_total", {
                importe: formatoImporte(negocio.importe_total),
              })}
            </p>
          ) : null}
          {cobros?.length ? (
            <ul className="text-sm space-y-1" data-testid="cobros">
              {cobros.map((c) => (
                <li key={String(c.id)}>
                  {translate("nevora.proyectos.cobro_n", { n: c.numero })}:{" "}
                  <span className="font-medium">
                    {formatoImporte(Number(c.importe))}
                  </span>{" "}
                  {translate("nevora.proyectos.sin_iva")} ·{" "}
                  {formatoFecha(c.cobrado_el)}
                  {c.concepto === "mantenimiento"
                    ? ` · ${translate("nevora.cobro.concepto_mantenimiento")}`
                    : ""}
                  {c.nota ? ` · ${c.nota}` : ""}
                  {(() => {
                    const delCobro = (comisiones ?? []).filter(
                      (k) => String(k.cobro_id) === String(c.id),
                    );
                    // Sin la 40 (o mientras carga), la del setter que guarda el cobro.
                    const lineas = delCobro.length
                      ? delCobro
                      : [
                          {
                            sale_id: c.comision_para ?? null,
                            pct: c.comision_pct ?? null,
                            importe: Number(c.comision_importe ?? 0),
                            motivo: c.comision_motivo ?? null,
                          },
                        ];
                    return lineas.map((k, i) => (
                      <span
                        key={i}
                        className="block text-xs text-muted-foreground"
                        data-testid="comision-cobro"
                      >
                        {textoComision(k, translate, nombre)}
                      </span>
                    ));
                  })()}
                </li>
              ))}
              <li className="text-muted-foreground">
                {translate("nevora.proyectos.total_cobrado", {
                  importe: formatoImporte(totalCobrado),
                })}
              </li>
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">
              {translate("nevora.proyectos.sin_cobros")}
            </p>
          )}
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setCobroAbierto(true)}
          >
            {translate("nevora.negocios.registrar_cobro")}
          </Button>
        </div>
      ) : null}

      {esAdmin && !hayVentas && proyectos ? (
        <div className="space-y-2 border-t pt-4">
          <h3 className="etiqueta">
            {translate("nevora.negocios.nuevo_proyecto")}
          </h3>
          <p className="text-xs text-muted-foreground">
            {translate("nevora.proyectos.nuevo_ayuda")}
          </p>
          <div className="flex flex-wrap items-end gap-2">
            <div className="space-y-1 flex-1 min-w-48">
              <Label htmlFor="proyecto_nuevo_nombre">
                {translate("nevora.proyectos.nombre")}
              </Label>
              <Input
                id="proyecto_nuevo_nombre"
                value={nombreNuevo}
                onChange={(e) => setNombreNuevo(e.target.value)}
                placeholder={translate("nevora.proyectos.nombre_ayuda")}
              />
            </div>
            <Button
              type="button"
              variant="secondary"
              disabled={creando}
              onClick={crearProyecto}
            >
              <Plus className="size-4 mr-1" />
              {translate("nevora.proyectos.crear")}
            </Button>
          </div>
        </div>
      ) : null}

      <CobroDialog
        proyecto={negocio}
        primero={negocio.embudo === "ventas"}
        open={cobroAbierto}
        onOpenChange={setCobroAbierto}
      />
    </section>
  );
};
